import { useEffect, useState } from 'react';
import type { Draft, DraftMode, Folder, MailAction, MailAttachment, MailDetail, MailThreadItem } from '@jerome2/shared';
import { api } from '../api.ts';
import { ARM_MS, isArmed, nextArm, type Armed } from '../lib/armed.ts';
import { fileKind, fileSize, fullDate, orgOf } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import { mailStore } from '../state/mail.ts';
import { ContextMenu, type MenuItem } from './ContextMenu.tsx';
import { HtmlBody } from './HtmlBody.tsx';
import { toolbarItems } from './toolbar.ts';
import { mailVerbs } from './verbs.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, IconButton } from '../ui/primitives.tsx';

// De lezer: kop, acties, bijlagen als tegels, de mail zelf, en de latere mails in het gesprek.
// Rechtsklik op de kop opent hetzelfde actiemenu als in de lijst; het zonnetje zet de mail in
// (of haalt hem uit) de einde-dag-samenvatting. Een concept (map Concepten) krijgt bovenaan een
// balk met Verzenden (twee klikken) en Bewerken, met de herkomst (concept van Jerome of eigen).
// De leesbreedte (ticket 28625): met de lijst ernaast hooguit 860 px, in de vergrote stand (`wide`: de lijst verborgen op het
// tabblad Mail, of het vergrote mailpaneel in de chat) hooguit 1200 px; de compacte lezer heeft daarbuiten geen grens.

/** De knop Lezer vergroten in de werkbalk (tabblad Mail, desktop; ticket 28625): `enlarged` is de stand met de lijst verborgen,
 * dan heet de knop Lijst tonen. Het mailpaneel in de chat geeft hem niet mee. */
export interface EnlargeKnob {
  enlarged: boolean;
  onToggle: () => void;
}

/** Hooguit zo breed staat de mail: met de lijst ernaast, of in de vergrote stand; compact zonder grens. */
const readWidth = (wide: boolean | undefined, compact: boolean | undefined): string => (wide ? 'max-w-[1200px]' : compact ? '' : 'max-w-[860px]');

export function Reader(props: { detail: MailDetail; onAction: (action: MailAction, folderId?: string, folderName?: string) => void; onCompose: (mode: DraftMode) => void; onOpenAttachment: (a: MailAttachment) => void; compact?: boolean | undefined; wide?: boolean | undefined; enlarge?: EnlargeKnob | undefined; onEod?: ((mark: boolean) => void) | undefined; onInstruct?: (() => void) | undefined; onContextMenu?: ((x: number, y: number) => void) | undefined; onEditDraft?: ((draftId: string) => void) | undefined; onSendDraft?: ((draftId: string) => Promise<void>) | undefined }) {
  const d = props.detail;
  const folders = useStore(mailStore, (s) => s.folders);
  const folder = folders.find((f) => f.id === d.parentFolderId);
  // Beantwoord of doorgestuurd (of allebei): uit Outlook en uit de latere eigen mails in het gesprek.
  const verbs = mailVerbs(d, d.thread.later);
  const [threadOpen, setThreadOpen] = useState(false);
  useEffect(() => setThreadOpen(false), [d.id]);
  const visibleAttachments = d.attachments.filter((a) => !a.isInline);
  // Compact (telefoon, mailpaneel) staan de bijlagen over de volle breedte; in de vergrote stand als tegels naast elkaar.
  const fullTiles = props.compact && !props.wide;

  return (
    <article className={`${props.compact ? 'px-4 pt-4 pb-6' : 'px-6 pt-6 pb-8'} ${readWidth(props.wide, props.compact)}`.trim()}>
      {d.isDraft ? <DraftBanner id={d.id} onEdit={props.onEditDraft} onSend={props.onSendDraft} /> : null}
      <div
        className="flex flex-wrap items-start gap-3"
        onContextMenu={(e) => {
          if (!props.onContextMenu) return;
          e.preventDefault();
          props.onContextMenu(e.clientX, e.clientY);
        }}
      >
        {/* De titel is naast de werkbalk minstens 18rem breed; past dat niet, dan gaat de werkbalk eronder. Smaller brak een lang
            woord midden door, en sinds ticket 28625 kan de lezer op elke breedte staan. */}
        <h1 className={`font-display ${props.compact ? 'text-[22px]' : 'text-[28px]'} leading-tight text-ink flex-1 min-w-[18rem] break-words`}>{d.subject || '(geen onderwerp)'}</h1>
        {/* Op de telefoon staan de acties in de kop erboven (PhoneReaderBar) en in het actievel. */}
        {props.compact ? null : <Toolbar detail={d} folders={folders} onAction={props.onAction} onCompose={props.onCompose} onEod={props.onEod} onInstruct={props.onInstruct} enlarge={props.enlarge} />}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-ink-soft">
        <span>
          <span className="font-medium text-ink">{d.from?.name || d.from?.address || 'Onbekend'}</span> {d.from ? <span className="text-muted">{orgOf(d.from.address)}</span> : null}
        </span>
        {d.to.length ? (
          <span>
            aan <span className="text-ink">{d.to.map((t) => t.name || t.address).join(', ')}</span>
          </span>
        ) : null}
        {d.cc.length ? (
          <span>
            cc <span className="text-ink">{d.cc.map((t) => t.name || t.address).join(', ')}</span>
          </span>
        ) : null}
        <span className="text-muted">{fullDate(d.received)}</span>
        {verbs.replied !== null ? (
          <span className="inline-flex items-center gap-1 text-accent" title={verbs.replied ? `Beantwoord op ${fullDate(verbs.replied)}` : 'Beantwoord'}>
            <Icon name="reply" size={13} /> beantwoord
          </span>
        ) : null}
        {verbs.forwarded !== null ? (
          <span className="inline-flex items-center gap-1 text-accent" title={verbs.forwarded ? `Doorgestuurd op ${fullDate(verbs.forwarded)}` : 'Doorgestuurd'}>
            <Icon name="forward" size={13} /> doorgestuurd
          </span>
        ) : null}
        {d.flagged ? (
          <span className="inline-flex items-center gap-1 text-red">
            <Icon name="flag" size={13} /> gevlagd
          </span>
        ) : null}
        {d.eodDay ? (
          <span className="inline-flex items-center gap-1 text-amber" title={`Zit in de einde-dag-samenvatting van ${d.eodDay}`}>
            <Icon name="sun" size={13} /> einde dag
          </span>
        ) : null}
        {folder ? (
          <span className="inline-flex items-center gap-1 text-muted" title={folder.path}>
            <Icon name="folder" size={13} /> {folder.name}
          </span>
        ) : null}
        {d.urgency ? <span className="text-muted" title={d.urgencyReason ?? ''}>urgentie {d.urgency}/5</span> : null}
      </div>
      {visibleAttachments.length ? (
        <ul className="mt-4 flex flex-wrap gap-2" aria-label="Bijlagen">
          {visibleAttachments.map((a) => (
            <li key={a.id} className={fullTiles ? 'w-full' : ''}>
              <button type="button" onClick={() => props.onOpenAttachment(a)} className={`flex items-center gap-3 rounded-lg border border-line bg-surface hover:bg-paper px-3 py-2 text-left ${fullTiles ? 'w-full' : ''}`}>
                <span className="h-9 w-9 shrink-0 rounded-md bg-tint text-ink-soft text-[11px] font-semibold flex items-center justify-center">{fileKind(a.name, a.contentType)}</span>
                <span className="min-w-0">
                  <span className={`block truncate text-[14px] text-ink ${fullTiles ? '' : 'max-w-[220px]'}`}>{a.name}</span>
                  <span className="block text-[12px] text-muted">{fileSize(a.size)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-5">
        <HtmlBody messageId={d.id} html={d.bodyHtml} text={d.bodyText} attachments={d.attachments} />
      </div>
      {d.thread.later.length ? (
        <div className="mt-6 border-t border-line pt-3">
          <button type="button" className="flex items-center gap-2 text-[14px] text-ink-soft hover:text-ink pointer-coarse:min-h-11" onClick={() => setThreadOpen((o) => !o)} aria-expanded={threadOpen}>
            <Icon name="chevron" size={14} className={threadOpen ? 'rotate-90' : ''} />
            Later in dit gesprek: {d.thread.later.length} {d.thread.later.length === 1 ? 'bericht' : 'berichten'}
          </button>
          {threadOpen ? (
            <ol className="mt-3 space-y-4">
              {d.thread.later.map((m) => (
                <ThreadItem key={m.id} item={m} />
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

/** Balk boven een concept: herkomst (Jerome of eigen), Verzenden met twee klikken, en Bewerken voor de editor. */
function DraftBanner(props: { id: string; onEdit?: ((draftId: string) => void) | undefined; onSend?: ((draftId: string) => Promise<void>) | undefined }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [armed, setArmed] = useState<Armed | null>(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setDraft(null);
    setArmed(null);
    api<Draft>(`/api/drafts/${encodeURIComponent(props.id)}`)
      .then((d) => !cancelled && setDraft(d))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [props.id]);
  const fromJerome = !!draft && draft.originalText !== null;
  const armedNow = isArmed(armed, Date.now());
  const recipients = draft?.to.map((a) => a.name || a.address).join(', ') || 'de ontvangers';
  const send = async () => {
    if (!props.onSend || sending) return;
    const step = nextArm(armed, Date.now());
    setArmed(step.state);
    if (!step.fire) {
      window.setTimeout(() => setArmed(null), ARM_MS);
      return;
    }
    setSending(true);
    try {
      await props.onSend(props.id);
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mb-5 flex items-start gap-3 rounded-lg bg-tint px-4 py-3 text-[14px] text-ink" role="status" aria-label="Concept">
      <Icon name="pen" size={16} className="text-accent shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p>
          {fromJerome ? (
            <>
              <span className="font-medium">Concept van Jerome</span>, nog niet verzonden. Verzend het hier, of pas het eerst aan; van je aanpassingen leert hij.
            </>
          ) : (
            <>
              <span className="font-medium">Concept</span>, nog niet verzonden. Verzend het hier, of bewerk het eerst.
            </>
          )}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {props.onSend ? (
            <Button variant="primary" size="sm" icon="send" onClick={() => void send()} busy={sending} disabled={!draft} className={armedNow ? 'ring-2 ring-amber ring-offset-1' : ''}>
              {armedNow ? 'Nu verzenden' : 'Verzenden'}
            </Button>
          ) : null}
          {props.onEdit ? (
            <Button size="sm" icon="pen" onClick={() => props.onEdit?.(props.id)}>
              Bewerken
            </Button>
          ) : null}
          {armedNow ? <span className="text-[13px] text-amber">Nog eens klikken verzendt naar {recipients}.</span> : null}
        </div>
      </div>
    </div>
  );
}

function ThreadItem(props: { item: MailThreadItem }) {
  const m = props.item;
  const detail = useStore(mailStore, (s) => s.details[m.id]?.detail ?? null);
  return (
    <li className="rounded-lg border border-line p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 text-[13px]">
        <span className="font-medium text-ink">{m.fromMe ? 'Jij' : m.from?.name || m.from?.address}</span>
        <span className="text-muted">{fullDate(m.received)}</span>
      </div>
      <div className="mt-2 text-[14px]">{detail ? <HtmlBody messageId={detail.id} html={detail.bodyHtml} text={detail.bodyText} attachments={detail.attachments} /> : <p className="text-ink-soft whitespace-pre-wrap">{m.preview}</p>}</div>
    </li>
  );
}

/** Skelet in de vorm van een mail, zodat de lezer niet van leeg naar vol springt terwijl de mail wordt opgehaald; `wide`: zo breed
 * als de mail in de vergrote stand. */
export function ReaderSkeleton(props: { wide?: boolean | undefined } = {}) {
  const bar = (w: string, h = 'h-3.5') => <div className={`${h} ${w} rounded bg-tint`} />;
  return (
    <div className={`px-6 pt-6 pb-8 ${readWidth(props.wide, false)} min-h-[60vh] animate-pulse`} role="status" aria-label="Mail ophalen">
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          {bar('w-3/4', 'h-7')}
          {bar('w-1/2', 'h-7')}
        </div>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 w-9 rounded-md bg-tint" />
          ))}
        </div>
      </div>
      <div className="mt-4 flex gap-3">
        {bar('w-40')}
        {bar('w-24')}
        {bar('w-32')}
      </div>
      <div className="mt-8 space-y-3">
        {bar('w-full')}
        {bar('w-11/12')}
        {bar('w-4/5')}
        {bar('w-full')}
        {bar('w-2/3')}
        <div className="h-3" />
        {bar('w-full')}
        {bar('w-5/6')}
        {bar('w-3/4')}
      </div>
    </div>
  );
}

const COMPOSE = new Set(['reply', 'replyAll', 'forward']);

/** Werkbalk van de lezer (desktop): de vaste acties als knoppen, de rest onder Meer (zie toolbar.ts), en op het tabblad Mail
 * daarachter de knop Lezer vergroten (`enlarge`, ticket 28625; het mailpaneel in de chat geeft hem niet mee). */
export function Toolbar(props: { detail: MailDetail; folders: Folder[]; onAction: (action: MailAction, folderId?: string, folderName?: string) => void; onCompose: (mode: DraftMode) => void; onEod?: ((mark: boolean) => void) | undefined; onInstruct?: (() => void) | undefined; enlarge?: EnlargeKnob | undefined }) {
  // Open: de plek van de knop Meer, waaronder het menu hangt. Een andere mail sluit het menu.
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const d = props.detail;
  useEffect(() => setAnchor(null), [d.id]);
  const { primary, more } = toolbarItems(d, props.folders, { compose: props.onCompose, action: props.onAction, eod: props.onEod, instruct: props.onInstruct });
  const button = (it: MenuItem) => {
    const flag = it.id === 'flag';
    return <IconButton key={it.id} icon={it.icon ?? 'more'} label={it.label} active={flag ? d.flagged : undefined} className={flag && d.flagged ? 'text-red' : ''} onClick={() => it.onSelect?.()} />;
  };
  const compose = primary.filter((it) => COMPOSE.has(it.id));
  const rest = primary.filter((it) => !COMPOSE.has(it.id));
  // min-w-0: op een smal scherm krimpt de rij mee en lopen de knoppen om in plaats van buiten beeld te staan.
  return (
    <div className="flex flex-wrap items-center gap-0.5 min-w-0 -mt-1" role="toolbar" aria-label="Acties">
      {compose.map(button)}
      {compose.length && rest.length ? <span className="w-px h-6 bg-line mx-1" /> : null}
      {rest.map(button)}
      {more.length ? (
        <>
          {/* Menuknop: aria-expanded zegt of het menu open is, dus geen aria-pressed. */}
          <IconButton icon="more" label="Meer acties" aria-haspopup="menu" aria-expanded={anchor !== null} active={anchor !== null} aria-pressed={undefined} onClick={(e) => setAnchor(anchor ? null : e.currentTarget.getBoundingClientRect())} />
          {anchor ? <ContextMenu anchor={anchor} items={more} label="Meer acties" onClose={() => setAnchor(null)} /> : null}
        </>
      ) : null}
      {props.enlarge ? (
        <>
          {/* Een knop over de indeling, geen mailactie: een lijn ervoor. Het label zegt wat hij doet, dus geen aria-pressed. */}
          <span className="w-px h-6 bg-line mx-1" />
          <IconButton icon={props.enlarge.enlarged ? 'shrink' : 'expand'} label={props.enlarge.enlarged ? 'Lijst tonen' : 'Lezer vergroten'} onClick={props.enlarge.onToggle} />
        </>
      ) : null}
    </div>
  );
}
